import type { LifecycleStageCount } from '@/services/dashboard.service';

// Reuses the exact status color convention already established for inspection
// status badges elsewhere in the app (QueueTable.tsx, InspectionListPage.tsx) —
// so a status means the same color everywhere, not a chart-specific palette.
const STAGE_ORDER = [
  'UNASSIGNED',
  'SCHEDULED',
  'ASSIGNED',
  'IN_PROGRESS',
  'TRANSFER_REQUESTED',
  'COMPLETED',
  'REVIEWED',
  'CLOSED',
  'CANCELLED',
] as const;

const STAGE_LABEL: Record<string, string> = {
  UNASSIGNED: 'Unassigned',
  SCHEDULED: 'Scheduled',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  TRANSFER_REQUESTED: 'Transfer Requested',
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const STAGE_BAR_COLOR: Record<string, string> = {
  UNASSIGNED: 'bg-slate-500',
  SCHEDULED: 'bg-sky-500',
  ASSIGNED: 'bg-blue-500',
  IN_PROGRESS: 'bg-amber-500',
  TRANSFER_REQUESTED: 'bg-orange-500',
  COMPLETED: 'bg-teal-500',
  REVIEWED: 'bg-purple-500',
  CLOSED: 'bg-green-500',
  CANCELLED: 'bg-red-500',
};

interface LifecycleStageChartProps {
  data: LifecycleStageCount[];
}

export function LifecycleStageChart({ data }: LifecycleStageChartProps) {
  const byStatus = new Map(data.map((d) => [d.status, d.count]));
  const rows = STAGE_ORDER.map((status) => ({ status, count: byStatus.get(status) ?? 0 })).filter(
    (r) => r.count > 0,
  );
  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-foreground">Jobs by Lifecycle Stage</h3>
      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground text-center">No active jobs yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.map((row) => {
            const widthPct = Math.max(4, Math.round((row.count / maxCount) * 100));
            return (
              <div
                key={row.status}
                className="group"
                title={`${STAGE_LABEL[row.status]}: ${row.count}`}
              >
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{STAGE_LABEL[row.status]}</span>
                  <span className="tabular-nums text-muted-foreground">{row.count}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-muted">
                  <div
                    className={`h-3 rounded-full ${STAGE_BAR_COLOR[row.status]} transition-all group-hover:opacity-80`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LifecycleStageChartSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="h-4 w-40 rounded skeleton-shimmer" />
      <div className="mt-5 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-3 w-24 rounded skeleton-shimmer" />
            <div className="h-3 w-full rounded-full skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  );
}
